import * as m from '@staiter/i18n';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute, useNavigate, useSearch } from '@tanstack/react-router';
import { ExternalLink, Plus } from 'lucide-react';
import { useEffect, useState } from 'react';
import { z } from 'zod';
import {
  EntityRefCell,
  ExportMenu,
  ListPageShell,
  PageHeader,
  SavedViewsBar,
  Table,
} from '~/components/data';
import { ItemPicker, UserPicker } from '~/components/domain';
import { useToast } from '~/components/feedback';
import {
  Button,
  DateRangePicker,
  Input,
  Radio,
  RadioGroup,
  SavedViewsManager,
  Select,
} from '~/components/primitives';
import { type WoBulkAction, WoBulkActionDialog, WoStatePill } from '~/components/work-orders';
import { type WoListFilter, bulkWorkOrderAction, fetchWorkOrders } from '~/lib/api/work-orders';
import { formatDate, formatDecimal } from '~/lib/format';
import { useListPageState } from '~/lib/hooks/useListPageState';
import { type SelectionEntities, rememberSelectionEntities } from '~/lib/work-orders/bulk-approver';
import { useActiveEntityStore } from '~/store/active-entity';
import shared from './_auth._shared.module.css';
import styles from './_auth.work-orders.index.module.css';

// U15.4 — search param `?error=wo_not_found` показывает toast при redirect с detail-page (U8.2).
const ListSearchSchema = z.object({ error: z.string().optional() });
export const Route = createFileRoute('/_auth/work-orders/')({
  staticData: { minMainWidth: 960 },
  component: WorkOrdersList,
  validateSearch: (s: Record<string, unknown>) => ListSearchSchema.parse(s),
});

const WARN_TYPES = [
  'insufficient_stock',
  'batch_expiry',
  'batch_expiry_imminent',
  'batch_expired',
  'bom_obsolete',
  'bom_alternate_used',
  'bom_scrap_exceeded',
  'routing_missing',
  'capacity_exceeded',
  'short_material',
  'cost_variance',
  'cost_variance_high',
  'job_card_missing',
  'no_active_bom_version',
] as const;

function warnSample(): string {
  // U15.3 — первые 3 типа warnings для tooltip-preview.
  return WARN_TYPES.slice(0, 3)
    .map((type) => m.route_wo_warn_label({ type }))
    .join(', ');
}

function WorkOrdersList(): JSX.Element {
  const nav = useNavigate();
  const toast = useToast();
  const qc = useQueryClient();
  const search = useSearch({ from: '/_auth/work-orders/' });
  const [filter, setFilter] = useState<WoListFilter>({});
  /*
   * F103 (С-I, гейт круга 1, UX MAJOR; NC-104): режим «Дата старта по плану / факту» — своё состояние,
   * а не вывод из введённых дат: без фактических дат «факту» не выбиралось ни кликом, ни стрелкой, и
   * период молча фильтровал по плану. Сохранённый вид с датами задаёт режим по тому, какие даты в нём.
   */
  const [startMode, setStartMode] = useState<'planned' | 'actual'>('planned');
  useEffect(() => {
    if (filter.actual_start_from || filter.actual_start_to) setStartMode('actual');
    else if (filter.planned_start_from || filter.planned_start_to) setStartMode('planned');
  }, [
    filter.actual_start_from,
    filter.actual_start_to,
    filter.planned_start_from,
    filter.planned_start_to,
  ]);

  // U15.4 — показываем toast при ?error=wo_not_found (U8.2 redirect).
  useEffect(() => {
    if (search.error === 'wo_not_found') {
      toast.push({
        variant: 'error',
        title: m.route_wo_index_error_wo_not_found_title(),
        description: m.route_wo_index_error_wo_not_found_desc(),
      });
      // Чистим search-param чтобы toast не показывался после refresh.
      void nav({ search: {} as never, replace: true });
    }
  }, [search.error, toast, nav]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  // С-I7 Р.3 (ACC-B40): компания каждого выбранного заказа — в момент выбора; выбор переживает смену
  // фильтра, а строк прежней выборки уже нет.
  const [selectedEntities, setSelectedEntities] = useState<SelectionEntities>(new Map());
  const [bulkDlg, setBulkDlg] = useState<WoBulkAction | null>(null);

  const q = useQuery({
    queryKey: ['work-orders', filter],
    queryFn: () => fetchWorkOrders(filter),
  });
  const changeSelection = (next: Set<string>) => {
    setSelected(next);
    setSelectedEntities((prev) => rememberSelectionEntities(prev, next, q.data?.items ?? []));
  };
  const state = useListPageState(q);
  // F-sync — колонка «Компания» только в агрегатном охвате (холдинг/«Все компании»).
  const aggregateScope = useActiveEntityStore((s) => s.activeScope) !== 'entity';
  const entities = q.data?.entities ?? {};

  const bulkMut = useMutation({
    mutationFn: bulkWorkOrderAction,
    onSuccess: (res) => {
      const ok = res.results.filter((r) => r.ok).length;
      const fail = res.results.length - ok;
      toast.push({
        variant: fail === 0 ? 'success' : 'warning',
        title: m.route_wo_index_toast_bulk_result({ ok, fail }),
      });
      setBulkDlg(null);
      changeSelection(new Set());
      qc.invalidateQueries({ queryKey: ['work-orders'] });
    },
    onError: (err: Error) =>
      toast.push({
        variant: 'error',
        title: m.route_wo_index_toast_bulk_failed(),
        description: err.message,
      }),
  });

  const canBulk = selected.size > 0;

  // Keyboard: r=release selected (opens bulk dialog)
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
      if (e.key === 'r' && canBulk) setBulkDlg('release');
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [canBulk]);

  return (
    <div className={shared.page}>
      <PageHeader
        title={m.nav_work_orders()}
        description={m.route_wo_index_description()}
        actions={
          <>
            <Button
              variant="primary"
              leadingIcon={<Plus size={14} />}
              onClick={() => nav({ to: '/work-orders/new' })}
            >
              {m.route_wo_index_btn_new()}
            </Button>
            <ExportMenu
              resource="work-orders"
              filters={filter as Record<string, string | undefined>}
            />
          </>
        }
      />

      <SavedViewsBar
        resource="work-orders"
        currentFilters={filter}
        onApply={(v) => setFilter(v.filters as WoListFilter)}
      />
      <SavedViewsManager
        resource="work-orders"
        currentFilters={filter}
        onApply={(v) => setFilter(v.filters as WoListFilter)}
      />

      <div className={shared.filters}>
        <Select
          label={m.route_wo_index_filter_state()}
          value={filter.state ?? ''}
          onChange={(e) => setFilter({ ...filter, state: e.target.value || undefined })}
          options={[
            { value: '', label: m.route_wo_index_filter_all() },
            { value: 'planned', label: m.route_wo_state_label_list({ state: 'planned' }) },
            { value: 'released', label: m.route_wo_state_label_list({ state: 'released' }) },
            { value: 'in_progress', label: m.route_wo_state_label_list({ state: 'in_progress' }) },
            {
              value: 'paused_external',
              label: m.route_wo_state_label_list({ state: 'paused_external' }),
            },
            { value: 'completed', label: m.route_wo_state_label_list({ state: 'completed' }) },
            { value: 'closed', label: m.route_wo_state_label_list({ state: 'closed' }) },
            { value: 'cancelled', label: m.route_wo_state_label_list({ state: 'cancelled' }) },
          ]}
        />
        <ItemPicker
          label={m.route_wo_index_filter_item()}
          value={filter.item_id ?? ''}
          onChange={(v) => setFilter({ ...filter, item_id: v || undefined })}
        />
        {/* U15.2 — явная семантика: план или факт; canonical поля W19.1. */}
        <div className={styles.startModeWrap}>
          <fieldset className={styles.startModeFieldset}>
            <legend className={styles.startModeLegend} id="wo-start-mode-legend">
              {m.route_wo_index_filter_start_mode_legend()}
            </legend>
            <RadioGroup orientation="horizontal" aria-labelledby="wo-start-mode-legend">
              <Radio
                className={styles.startModeLabel}
                name="startDateMode"
                checked={startMode === 'planned'}
                onChange={() => {
                  setStartMode('planned');
                  // Введённый период переходит в плановые даты — смысл фильтра меняется явно.
                  setFilter({
                    ...filter,
                    planned_start_from: filter.actual_start_from ?? filter.planned_start_from,
                    planned_start_to: filter.actual_start_to ?? filter.planned_start_to,
                    actual_start_from: undefined,
                    actual_start_to: undefined,
                  });
                }}
                label={m.route_wo_index_filter_start_mode_planned()}
              />
              <Radio
                className={styles.startModeLabel}
                name="startDateMode"
                checked={startMode === 'actual'}
                onChange={() => {
                  setStartMode('actual');
                  setFilter({
                    ...filter,
                    actual_start_from: filter.planned_start_from ?? filter.actual_start_from,
                    actual_start_to: filter.planned_start_to ?? filter.actual_start_to,
                    planned_start_from: undefined,
                    planned_start_to: undefined,
                  });
                }}
                label={m.route_wo_index_filter_start_mode_actual()}
              />
            </RadioGroup>
          </fieldset>
          <DateRangePicker
            label={m.route_wo_index_filter_start_period()}
            value={{
              from: filter.actual_start_from ?? filter.planned_start_from ?? null,
              to: filter.actual_start_to ?? filter.planned_start_to ?? null,
            }}
            onChange={(r) => {
              setFilter({
                ...filter,
                ...(startMode === 'planned'
                  ? {
                      planned_start_from: r.from ?? undefined,
                      planned_start_to: r.to ?? undefined,
                      actual_start_from: undefined,
                      actual_start_to: undefined,
                    }
                  : {
                      actual_start_from: r.from ?? undefined,
                      actual_start_to: r.to ?? undefined,
                      planned_start_from: undefined,
                      planned_start_to: undefined,
                    }),
                // deprecated period_* — убираем.
                period_from: undefined,
                period_to: undefined,
              });
            }}
          />
        </div>
        <UserPicker
          label={m.route_wo_index_filter_executor()}
          value={filter.executor_id ?? ''}
          onChange={(v) => setFilter({ ...filter, executor_id: v || undefined })}
        />
        <Input
          label={m.route_wo_index_filter_bom_version()}
          value={filter.bom_version ?? ''}
          onChange={(e) => setFilter({ ...filter, bom_version: e.target.value || undefined })}
        />
      </div>

      {canBulk && (
        <div
          role="toolbar"
          aria-label={m.route_wo_index_bulk_toolbar_aria()}
          className={styles.bulkToolbar}
        >
          <strong>{m.route_wo_index_bulk_selected_count({ count: selected.size })}</strong>
          <Button size="sm" onClick={() => setBulkDlg('release')}>
            {m.route_wo_index_btn_release()}
          </Button>
          <Button size="sm" variant="destructive" onClick={() => setBulkDlg('cancel')}>
            {m.route_wo_index_btn_cancel()}
          </Button>
          <Button size="sm" variant="secondary" onClick={() => setBulkDlg('reassign')}>
            {m.route_wo_index_btn_reassign()}
          </Button>
          <Button size="sm" variant="ghost" onClick={() => changeSelection(new Set())}>
            {m.route_wo_index_btn_clear_selection()}
          </Button>
        </div>
      )}

      <ListPageShell
        state={state}
        emptyTitle={m.route_wo_index_empty_title()}
        emptyDescription={m.route_wo_index_empty_description()}
        emptyAction={
          <Link to="/work-orders/new">
            <Button variant="primary" leadingIcon={<Plus size={14} />}>
              {m.route_wo_index_btn_new()}
            </Button>
          </Link>
        }
      >
        {(rows) => (
          <Table
            ariaLabel={m.route_wo_index_aria()}
            data={rows}
            rowKey={(w) => w.id}
            selectable
            selectedIds={selected}
            onSelectionChange={changeSelection}
            stickyFirstColumn
            columns={[
              {
                id: 'code',
                header: m.route_wo_index_col_code(),
                sticky: true,
                accessor: (w) => (
                  <Link to="/work-orders/$id" params={{ id: w.id }} className={shared.link}>
                    <span className={shared.mono}>{w.code}</span>
                  </Link>
                ),
              },
              ...(aggregateScope
                ? [
                    {
                      id: 'entity',
                      header: m.project_col_entity(),
                      accessor: (w: (typeof rows)[number]) =>
                        entities[w.entity_id ?? '']?.name ?? '—',
                      width: '180px',
                    },
                  ]
                : []),
              {
                id: 'item',
                header: m.route_wo_index_col_item(),
                accessor: (w) => <EntityRefCell name={w.item_name} code={w.item_id.slice(0, 8)} />,
              },
              {
                id: 'qty',
                header: m.route_wo_index_col_qty(),
                // FE-223: formatDecimal trim trailing zeros (10 digits → 1-4).
                accessor: (w) => formatDecimal(w.qty),
                mono: true,
                align: 'right',
              },
              {
                id: 'state',
                header: m.route_wo_index_col_state(),
                accessor: (w) => <WoStatePill state={w.state} />,
              },
              {
                // FE-N Pass 77: formatDate вместо raw ISO slice (показывало «2026-05-03» вместо «03.05.2026»).
                id: 'plan_start',
                header: m.route_wo_index_col_plan_start(),
                accessor: (w) => (w.planned_start_at ? formatDate(w.planned_start_at) : '—'),
                mono: true,
              },
              {
                id: 'plan_end',
                header: m.route_wo_index_col_plan_end(),
                accessor: (w) => (w.planned_end_at ? formatDate(w.planned_end_at) : '—'),
                mono: true,
              },
              {
                id: 'executor',
                header: m.route_wo_index_col_executor(),
                accessor: (w) => w.executor_name ?? '—',
              },
              {
                id: 'warn',
                header: m.route_wo_index_col_warn(),
                align: 'right',
                accessor: (w) =>
                  // U15.3 — click → open WO detail на Warnings tab.
                  (w.warnings_count ?? 0) > 0 ? (
                    <Link
                      to="/work-orders/$id"
                      params={{ id: w.id }}
                      search={{ tab: 'warnings' as const }}
                      title={m.route_wo_index_warn_tooltip({
                        count: w.warnings_count ?? 0,
                        sample: warnSample(),
                      })}
                      className={styles.warnChip}
                    >
                      ⚠ {w.warnings_count}
                    </Link>
                  ) : (
                    '—'
                  ),
              },
            ]}
            rowActions={(w) => (
              <Button
                size="sm"
                variant="ghost"
                leadingIcon={<ExternalLink size={14} aria-hidden="true" />}
                onClick={(e) => {
                  e.stopPropagation();
                  window.open(`/work-orders/${w.id}`, '_blank', 'noopener');
                }}
                title={m.ui_otkryt_v_novoy_vkladke()}
                aria-label={m.ui_otkryt_v_novoy_vkladke()}
              >
                {m.ui_otkryt_2()}
              </Button>
            )}
          />
        )}
      </ListPageShell>

      <WoBulkActionDialog
        action={bulkDlg}
        selected={selected}
        entities={selectedEntities}
        entityNames={Object.fromEntries(
          Object.entries(entities).map(([id, e]) => [id, e?.name ?? id]),
        )}
        pending={bulkMut.isPending}
        onClose={() => setBulkDlg(null)}
        onSubmit={(input) => bulkMut.mutate(input)}
      />
    </div>
  );
}
